import {
  Button,
  Center,
  IconButton, Menu,
  MenuButton, MenuDivider, MenuGroup, MenuItem,
  MenuList, Tooltip,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { DataEntryPlanningModal } from "./components";
import { DeleteDialog } from "../DeleteDialog";
import { DataTable } from "../../../../components/DataTable";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import React, { useEffect, useMemo, useState } from "react";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import {
  getSortDirection,
  QUARTERS,
  QUESTIONNAIRE_STATUSES,
  showErrorToast,
  showSuccessToast,
  SORT_DIRECTIONS
} from "../../../../utils";
import {
  organizationAdministrationKeys,
  organizationQuestionnaireKeys,
  useCreateQuestionnaire,
  useDeleteQuestionnaire,
  useGetAllDataEntryPlanningQuestionnaires,
  useUnfinaliseOrgQuestionnaire,
  useUpdateQuestionnaire
} from "../../../../hooks/query";
import { FaBars, FaEye, FaPen, FaTrash, FaUndo } from "react-icons/fa";
import { CATEGORIES_STEPS } from "../../../OrganizationQuestionnaire/components/Categories/const";
import { useTranslation } from "react-i18next";
import { Page } from "../../../../components/Page";
import { Card } from "../../../../components/Card";
import { WithAuthorization } from "../../../../context";
import { ReviewQuestionnaireModal } from "../ReviewQuestionnaireModal";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../config";
import { MultiSelectFilter } from "../../../../components/DataTable/components";

export const getRequestedQuestionnaires = (questionnaireList) => {
  const listRaw = Object.keys(questionnaireList).filter(a => questionnaireList[a]);
  let questionnaireString = "";
  listRaw.map((q, index) => {
    const editedTitle = q.substring(0, q.indexOf("ShouldBeFilled"));
    let questionnaire = "";
    switch (editedTitle) {
      case "directEmissions":
        questionnaire = "Scope 1";
        break;
      case "indirectEmissions":
        questionnaire = "Scope 2";
        break;
      default:
        questionnaire = `Category ${CATEGORIES_STEPS.indexOf(editedTitle) + 1}`;
        break;
    }

    return questionnaireString = questionnaireString + questionnaire + `${index !== listRaw.length - 1 ? ", " : ""}`;
  });
  return questionnaireString;
};

function OrganizationPlanning() {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning"
  });

  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [createEditModalData, setCreateEditModalData] = useState(null);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState("");

  const {
    isOpen: isReviewQuestionnaireOpen,
    onOpen: onReviewQuestionnaireOpen,
    onClose: onReviewQuestionnaireClose
  } = useDisclosure();

  const [tasksList, setTasksList] = useState([]);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [`id,${SORT_DIRECTIONS.DESC}`],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const { totalPages, filterObj, queryPageIndex, queryPageSize, queryPageSortBy, totalElements } = dataTableState;

  const manageDataTableState = (newValObject) => {
    const calcState = (prevState) => {
      let changedAttributes = {};

      Object.keys(newValObject).forEach(dataTableAttribute => {
        const value = newValObject[dataTableAttribute];

        if (dataTableAttribute === "filterObj") {
          // We have to save not only the new value sent from the data table
          // but also the previously applied filters, which we get from prevState
          changedAttributes[dataTableAttribute] = {
            ...prevState[dataTableAttribute],
            [value.attribute]: value.value
          };
        } else if (dataTableAttribute === "queryPageSortBy") {
          let stringValue = value[0];
          let accessorName = stringValue.split(",")[0];
          const sortDirection = stringValue.split(",")[1];
          const updatedArray = [...prevState[dataTableAttribute]];

          if (accessorName === "assigneeName") {
            // Back-end requires the usage of 'user.email' instead of 'assigneeName' for sorting
            stringValue = `user.email,${sortDirection}`;
            accessorName = "user.email";
          } else if (accessorName === "year") {
            // Back-end requires the usage of 'period.year' instead of 'year' for sorting
            stringValue = `period.year,${sortDirection}`;
            accessorName = "period.year";
          } else if (accessorName === "quarter") {
            // Back-end requires the usage of 'period.quarter' instead of 'quarter' for sorting
            stringValue = `period.quarter,${sortDirection}`;
            accessorName = "period.quarter";
          }

          // Removing initial sorting by id, if still available
          const indexOfInitial = updatedArray.findIndex(el => {
            return el.split(",")[0] === "id";
          });
          if (indexOfInitial > -1) {
            updatedArray.splice(indexOfInitial, 1);
          }

          const indexOfExisting = updatedArray.findIndex(el => {
            return el.split(",")[0] === accessorName;
          });
          if (indexOfExisting > -1) {
            // Column which we try to sort is already in the sort list, so we just manipulate its record
            if (sortDirection === SORT_DIRECTIONS.NONE) {
              updatedArray.splice(indexOfExisting, 1);
            } else {
              updatedArray[indexOfExisting] = stringValue;
            }
          } else {
            // Column which we try to sort does not exist in the sort list, so we add it
            updatedArray.push(stringValue);
          }

          changedAttributes[dataTableAttribute] = updatedArray;
        } else {
          // All other values have primitive types, so we apply them directly
          // without checking their previous states
          changedAttributes[dataTableAttribute] = value;
        }
      });

      return changedAttributes;
    };

    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calcState(prevState)
      };
    });
  };

  const handleErrors = (error) => {
    let errorMsg = error.response?.status === 409 &&
    error.response?.data?.parameters?.resourceName === "OrganizationQuestionnaire" ?
      t("page.duplicatedAssignee", { typeOfQuestionnaire: "" }) : error.response?.status === 400 &&
      error.response?.data?.errorCode === "object-argument-not-valid" ? error.response?.data?.parameters?.message :
        error.message;

    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMsg);
  };

  const { mutate: create } = useCreateQuestionnaire({
    onSuccess: () => {
      handleModalClose();
      showSuccessToast(toast, t("messages.taskCreated"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      queryClient.invalidateQueries(organizationAdministrationKeys.user());
      refetchTasks();
      dispatch(hideAppSpinner());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: update } = useUpdateQuestionnaire({
    onSuccess: () => {
      handleModalClose();
      showSuccessToast(toast, t("messages.taskUpdated"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      refetchTasks();
      dispatch(hideAppSpinner());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: deleteTask } = useDeleteQuestionnaire({
    onSuccess: () => {
      onConfirmDialogClose();
      setSelectedRowId(null);
      showSuccessToast(toast, t("messages.taskDeleted"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      refetchTasks();
      dispatch(hideAppSpinner());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: unfinaliseQuestionnaire } = useUnfinaliseOrgQuestionnaire({
    onMutate: () => {
      dispatch(showAppSpinner());
    },
    onSuccess: () => {
      showSuccessToast(toast, t("messages.unfinaliseSuccess"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      refetchTasks();
      dispatch(hideAppSpinner());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  function onCreateNew() {
    setSelectedRowId(null);
    onOpen();
  }

  const onSubmit = (taskId, dataToSave) => {

    dispatch(showAppSpinner());
    if (taskId) {
      update({ id: taskId, data: dataToSave });
    } else {
      create(dataToSave);
    }
  };

  const columns = useMemo(() => {
      function handleEdit(row) {
        setCreateEditModalData(row.row.original);
        onOpen();
      }

      function handleDelete(rowId) {
        setSelectedRowId(rowId);
        onConfirmDialogOpen();
      }

      function handleViewQuestionnaire(recordId) {
        setSelectedQuestionnaireId(recordId);
        onReviewQuestionnaireOpen();
      }

      function handleEditQuestionnaire(questionnaireId) {
        navigate(`${routes.organisationQuestionnaire}?id=${questionnaireId}`);
      }

      function handleUnfinaliseQuestionnaire(questionnaireId) {
        unfinaliseQuestionnaire(questionnaireId);
      }

      return [
        {
          header: "Description",
          accessorKey: "description",
          size: 150,
          isSortable: false
        },
        {
          header: "Assignee",
          accessorKey: "assigneeName",
          size: 200,
          sortDirection: getSortDirection(queryPageSortBy, "user.email")
        },
        {
          header: "Year",
          accessorKey: "year",
          size: 130,
          sortDirection: getSortDirection(queryPageSortBy, "period.year")
        },
        {
          header: "Quarter",
          accessorKey: "quarter",
          size: 180,
          sortDirection: getSortDirection(queryPageSortBy, "period.quarter"),
          filter: {
            params: {
              options: Object.keys(QUARTERS).map(key => {
                return {
                  label: key,
                  value: key
                };
              }),
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          }
        },
        {
          header: "Status",
          accessorKey: "status",
          size: 220,
          sortDirection: getSortDirection(queryPageSortBy, "status"),
          filter: {
            params: {
              options: [
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.FINALIZED}`),
                  value: QUESTIONNAIRE_STATUSES.FINALIZED
                },
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.IN_PROGRESS}`),
                  value: QUESTIONNAIRE_STATUSES.IN_PROGRESS
                },
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.NEW}`),
                  value: QUESTIONNAIRE_STATUSES.NEW
                }
              ],
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          }
        },
        {
          header: "Questionnaires",
          accessorKey: "questionnaires",
          size: 170,
          enableColumnFilter: false,
          isSortable: false
        },
        {
          header: <div style={{ textAlign: "left" }}><b>Actions</b></div>,
          accessorKey: "action",
          size: 80,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy>
              <Tooltip label={t("page.actionsTooltip")}>
                <MenuButton
                  as={IconButton}
                  aria-label="Actions"
                  icon={<FaBars />}
                  variant="outline"
                />
              </Tooltip>
              <MenuList>
                <MenuGroup title={t("page.manageTaskGroupHeader")}>
                  <MenuItem
                    icon={<FaPen color="green" opacity="0.7" />}
                    onClick={() => handleEdit(row)}
                    isDisabled={!row.row.original.updatable}
                  >
                    {t("page.editTask")}
                  </MenuItem>
                  <MenuItem
                    icon={<FaTrash color="red" opacity="0.6" />}
                    onClick={() => handleDelete(row.row.original.id)}
                  >
                    {t("page.deleteTask")}
                  </MenuItem>
                </MenuGroup>
                <MenuDivider />
                <MenuGroup title={t("page.manageDataGroupHeader")}>
                  <MenuItem
                    icon={<FaEye color="green" opacity="0.7" />}
                    onClick={() => handleViewQuestionnaire(row.row.original.id)}
                  >
                    {t("page.viewEmissionsData")}
                  </MenuItem>
                  <MenuItem
                    icon={<FaPen color="green" opacity="0.7" />}
                    onClick={() => handleEditQuestionnaire(row.row.original.id)}
                    isDisabled={row.row.original.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED}
                    title={row.row.original.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED ?
                      t("page.finalisedQuestionnaireTooltip") : null}
                  >
                    {t("page.editEmissionsData")}
                  </MenuItem>
                  <MenuItem
                    icon={<FaUndo color="green" opacity="0.7" />}
                    onClick={() => handleUnfinaliseQuestionnaire(row.row.original.id)}
                    isDisabled={row.row.original.originalStatus !== QUESTIONNAIRE_STATUSES.FINALIZED}
                  >
                    {t("page.unfinaliseGHGQuestionnaire")}
                  </MenuItem>
                </MenuGroup>
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [t, onOpen, onConfirmDialogOpen, onReviewQuestionnaireOpen, navigate, unfinaliseQuestionnaire,
      queryPageSortBy, filterObj]
  );

  function onDelete() {
    dispatch(showAppSpinner());
    deleteTask(selectedRowId);
  }

  const constructQueryParams = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    // "status", "quarter" and "sort" params are extracted from queryParams object, because they can have more than one value.
    // To avoid overriding of those multiple values which have to share the same key ("status", "quarter" or "sort"), we put them
    // together in a string called "multipleValuesParams"
    let multipleValuesParams = "";

    Object.keys(queryParams).forEach(param => {
      if (queryParams[param] === "" || queryParams[param].length === 0) {
        // Removing empty params
        delete queryParams[param];
      } else if (param === "status") {
        queryParams[param].forEach(statusObj => {
          multipleValuesParams += `&status=${statusObj.value}`;
        });
        delete queryParams[param];
      } else if (param === "quarter") {
        queryParams[param].forEach(quarterObj => {
          multipleValuesParams += `&quarter=${quarterObj.value}`;
        });
        delete queryParams[param];
      } else if (param === "sort") {
        queryParams[param].forEach(sortStr => {
          multipleValuesParams += `&sort=${sortStr}`;
        });
        delete queryParams[param];
      }
    });

    return `${new URLSearchParams(queryParams).toString()}${multipleValuesParams}`;
  };

  const {
    isLoading: isLoadingTasks,
    isFetching: isFetchingTasks,
    refetch: refetchTasks
  } = useGetAllDataEntryPlanningQuestionnaires(constructQueryParams(),
    {
      onSuccess: (data) => {
        const pageContent = data?.content ?? [];
        const tasks = pageContent.map((task) => {
          return {
            id: task?.id,
            type: task?.type,
            description: task?.description,
            dimensionItems: task?.dimensionItems,
            assigneeName: task?.assigneeName,
            assigneeId: task?.assigneeId,
            inviteeEmail: task?.inviteeEmail,
            year: task?.period?.year?.toString() ?? "",
            quarter: task?.period?.quarter ?? "",
            status: t("status." + task?.status),
            questionnaires: getRequestedQuestionnaires(task.configurations),
            configurations: task?.configurations,
            period: task?.period,
            originalStatus: task?.status,
            updatable: task?.updatable
          };
        });
        setTasksList(tasks);
        if (pageContent.length === 0 && data?.number !== 0 && data?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when the last record on last page is deleted and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: data?.totalElements ?? 0,
            totalPages: data?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: data?.totalElements ?? 0,
            totalPages: data?.totalPages ?? 0
          });
        }
      },
      onError: err => handleErrors(err), cacheTime: 0,
      onSettled: () => {
        dispatch(hideAppSpinner());
      }
    }
  );

  const handleModalClose = () => {
    onClose();
    setCreateEditModalData(null);
  };

  useEffect(() => {
    refetchTasks();
  }, [refetchTasks, queryPageIndex, queryPageSize]);

  const closeReviewQuestionnaireModal = () => {
    setSelectedQuestionnaireId("");
    onReviewQuestionnaireClose();
  };

  return <Page title={t("page.title")} subTitle={t("page.subTitle")}>
    <Card>
      <Center marginBlockEnd="5">
        <Tooltip label={t("page.addNewTask.tooltip")}>
          <Button
            colorScheme="customScheme"
            width="40"
            onClick={onCreateNew}>
            + {t("page.addNewTask.title")}
          </Button>
        </Tooltip>
      </Center>

      {isOpen && <DataEntryPlanningModal
        modalData={createEditModalData}
        isOpen={isOpen}
        onClose={handleModalClose}
        onSubmit={onSubmit}
      />}

      <DeleteDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onDelete={onDelete}
        entityName={"Task"}
      />

      <ReviewQuestionnaireModal
        questionnaireId={selectedQuestionnaireId}
        isOpen={isReviewQuestionnaireOpen}
        onClose={closeReviewQuestionnaireModal}
      />

      <DataTable
        data={tasksList}
        columns={columns}
        isLoading={isLoadingTasks || isFetchingTasks}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
        queryPageSortBy={queryPageSortBy}
        setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
        filterObj={filterObj}
        setFilterObj={(newFilterObj) => {
          manageDataTableState({
            filterObj: newFilterObj,
            queryPageIndex: 0
          });
        }}
        enableFilters={true}
        enableSorting={true}
        containerHeight="var(--chakra-sizes-2xl)"
      />
    </Card>
  </Page>;
}

export default WithAuthorization(OrganizationPlanning);